import React, { useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Row, Col, Space, Select, Button } from 'antd';
import { useQuery } from '@apollo/client';
import PageLayout from '@components/layout/PageLayout';
import EstimationFactorDetails from '@components/estimation-factor-details/EstimationFactorDetails';
import { NSPDatePicker } from '@components/ui/NSPDatepicker';
import { useDateNavigation } from '@hooks/graphql/useDateNavigation';
import { ESTIMATION_FACTORS } from 'graphql/estimationFactors.gql';
import useSession from '@hooks/useSession';
import styles from './EstimationFactorDashboardPage.module.css';

const { Option } = Select;

const ENERGY_TYPES = [
  { value: 'solar', label: 'Solar' },
  { value: 'wind', label: 'Wind' },
];

const EstimationFactorDashboardPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAdmin, isSuperAdmin } = useSession();
  // Approver (Windvoice.Approver) has read-only access — no editing actions
  const isReadOnly = isAdmin && !isSuperAdmin;

  const {
    selectedPeriod,
    pickerValue,
    isNextDisabled,
    navigateMonth,
    handleDateChange,
  } = useDateNavigation();

  const [activeEnergyType, setActiveEnergyType] = useState(searchParams.get('energyType') || '');
  const [activeRegion, setActiveRegion] = useState(searchParams.get('region') || '');
  const [activePeriod, setActivePeriod] = useState(searchParams.get('month') || '');

  const [pendingEnergyType, setPendingEnergyType] = useState(searchParams.get('energyType') || '');
  const [pendingRegion, setPendingRegion] = useState(searchParams.get('region') || '');

  const { data: factorsData } = useQuery(ESTIMATION_FACTORS, {
    variables: { month_year: selectedPeriod },
    skip: !selectedPeriod,
  });

  const availableRegions: string[] = React.useMemo(() => {
    if (!factorsData?.estimationFactors?.data) return [];
    const set = new Set<string>();
    factorsData.estimationFactors.data.forEach((row: any) => {
      if (row.region) set.add(row.region.toUpperCase());
    });
    return Array.from(set).sort();
  }, [factorsData]);

  const handleDisplayData = useCallback(() => {
    if (!pendingRegion || !pendingEnergyType) return;
    setActiveRegion(pendingRegion);
    setActiveEnergyType(pendingEnergyType);
    setActivePeriod(selectedPeriod);
    setSearchParams({
      energyType: pendingEnergyType,
      region: pendingRegion,
      month: selectedPeriod,
    }, { replace: true });
  }, [pendingRegion, pendingEnergyType, selectedPeriod, setSearchParams]);

  return (
    <PageLayout
      title="Estimation Factor Dashboard"
      headerAccessoryView={
        <Row align="middle" gutter={[12, 8]} className={styles.header}>
          <Col>
            <Space size="middle" align="center" wrap>
              <Select
                placeholder="Select a Region"
                value={pendingRegion || undefined}
                onChange={(val) => setPendingRegion(val)}
                style={{ minWidth: 180 }}
                allowClear
              >
                {availableRegions.map((r) => (
                  <Option key={r} value={r.toLowerCase()}>{r}</Option>
                ))}
              </Select>

              <Select
                placeholder="Select Generation Type"
                value={pendingEnergyType || undefined}
                onChange={(val) => setPendingEnergyType(val)}
                style={{ minWidth: 200 }}
                allowClear
              >
                {ENERGY_TYPES.map((t) => (
                  <Option key={t.value} value={t.value}>{t.label}</Option>
                ))}
              </Select>

              <Button
                type="primary"
                onClick={handleDisplayData}
                disabled={!pendingRegion || !pendingEnergyType}
              >
                Display Data
              </Button>
            </Space>
          </Col>

          <Col>
            <NSPDatePicker
              picker="month"
              format="MMM YYYY"
              allowClear={false}
              value={pickerValue}
              onChange={handleDateChange}
              showNavigation
              showCalendarIcon
              onNavigateLeft={() => navigateMonth(-1)}
              onNavigateRight={() => navigateMonth(1)}
              isNextDisabled={isNextDisabled}
            />
          </Col>
        </Row>
      }
    >
      <EstimationFactorDetails
        energyType={activeEnergyType}
        region={activeRegion}
        month={activePeriod}
        readOnly={isReadOnly}
      />
    </PageLayout>
  );
};

export default EstimationFactorDashboardPage;
